import { useDisclosure } from "@mantine/hooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { SurfaceTreatmentFormModal } from "#modules/company/view/SurfaceTreatmentFormModal";
import { SurfaceTreatmentsCsvActions } from "#modules/company/view/SurfaceTreatmentsCsvActions";
import { SurfaceTreatmentsSectionTable } from "#modules/company/view/SurfaceTreatmentsSectionTable";
import { useCompany } from "#modules/company/view/useCompany";
import { CustomSurfaceTreatmentsDto } from "#modules/companySettings/infra/fetchCustomSurfaceTreatments.api";
import { useCustomSurfaceTreatments } from "#modules/companySettings/view/useCustomSurfaceTreatments";
import { useDeleteCustomSurfaceTreatment } from "#modules/companySettings/view/useDeleteCustomSurfaceTreatment";
import { Box } from "#shared/view/components/Box";
import { Space } from "#shared/view/components/Space";
import { Stack } from "#shared/view/components/Stack";
import { Title } from "#shared/view/components/Title";

export const SurfaceTreatmentsSection = () => {
  const { t } = useTranslation();
  const { customSurfaceTreatments } = useCustomSurfaceTreatments();
  const { company } = useCompany();
  const companyId = company?.id ?? "";

  const { deleteCustomSurfaceTreatment } = useDeleteCustomSurfaceTreatment();

  const [createModalOpened, { open: openCreateModal, close: closeCreateModal }] =
    useDisclosure(false);
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
  const [selectedTreatment, setSelectedTreatment] = useState<
    CustomSurfaceTreatmentsDto[number] | null
  >(null);

  const onAddRow = () => {
    setSelectedTreatment(null);
    openCreateModal();
  };

  const onEditRow = (rowId: string) => {
    const treatment = customSurfaceTreatments.find((item) => item.id === rowId) ?? null;
    setSelectedTreatment(treatment);
    openEditModal();
  };

  const onRemoveRow = (rowId: string) => {
    deleteCustomSurfaceTreatment({
      companyId,
      surfaceTreatmentId: rowId,
    });
  };

  return (
    <Stack gap={0}>
      <Title order={5}>{t("settings.surfaceTreatments.sectionTitle")}</Title>

      <Space h="sm" />
      <SurfaceTreatmentsCsvActions
        companyId={companyId}
        customSurfaceTreatments={customSurfaceTreatments}
      />
      <Space h="sm" />

      <Box miw={400} maw={1000} w="100%">
        <SurfaceTreatmentsSectionTable
          data={customSurfaceTreatments}
          onAddRow={onAddRow}
          onEditRow={onEditRow}
          onRemoveRow={onRemoveRow}
        />
      </Box>

      <SurfaceTreatmentFormModal
        opened={createModalOpened}
        onClose={closeCreateModal}
        companyId={companyId}
        mode="create"
      />
      {selectedTreatment && (
        <SurfaceTreatmentFormModal
          opened={editModalOpened}
          onClose={closeEditModal}
          companyId={companyId}
          mode="edit"
          initialData={selectedTreatment}
        />
      )}
    </Stack>
  );
};
